import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ShoppingBag, Loader2 } from "lucide-react";
import { ShopifyProduct } from "@/lib/shopify";
import { useCartStore } from "@/stores/cartStore";
import { useCurrency } from "@/contexts/CurrencyContext";
import { toast } from "sonner";

interface Props {
  product: ShopifyProduct | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const VariantPickerDialog = ({ product, open, onOpenChange }: Props) => {
  const addItem = useCartStore((s) => s.addItem);
  const isLoading = useCartStore((s) => s.isLoading);
  const { formatPrice } = useCurrency();
  const [selectedVariantId, setSelectedVariantId] = useState<string | null>(null);

  if (!product) return null;
  const { node } = product;
  const variants = node.variants.edges.map((e) => e.node);
  const image = node.images.edges[0]?.node;

  const handleAdd = async () => {
    const variant = variants.find((v) => v.id === selectedVariantId);
    if (!variant) return;
    await addItem({
      product,
      variantId: variant.id,
      variantTitle: variant.title,
      price: variant.price,
      quantity: 1,
      selectedOptions: variant.selectedOptions || [],
    });
    toast.success(`${node.title} (${variant.title}) added to cart`);
    setSelectedVariantId(null);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) setSelectedVariantId(null); }}>
      <DialogContent className="bg-card border-border max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-foreground">
            Choose your shade
          </DialogTitle>
          <DialogDescription className="font-subtitle text-muted-foreground">
            {node.title}
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-4 items-center py-2">
          {image && (
            <div className="w-20 h-20 rounded-lg overflow-hidden bg-secondary/40 flex-shrink-0">
              <img src={image.url} alt={node.title} className="w-full h-full object-cover" />
            </div>
          )}
          <div>
            <p className="font-subtitle text-lg font-semibold text-foreground">
              {formatPrice(parseFloat(node.priceRange.minVariantPrice.amount))}
            </p>
            <p className="text-xs font-subtitle text-muted-foreground">
              {variants.length} options available
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-2 max-h-72 overflow-y-auto pr-1">
          {variants.map((v) => {
            const selected = selectedVariantId === v.id;
            const disabled = !v.availableForSale;
            return (
              <button
                key={v.id}
                disabled={disabled}
                onClick={() => setSelectedVariantId(v.id)}
                className={`text-left p-3 rounded-lg border transition-all ${
                  selected
                    ? "border-primary bg-primary/10 shadow-[0_0_20px_hsl(350,100%,88%,0.25)]"
                    : "border-border bg-secondary/30 hover:border-primary/40"
                } ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-subtitle text-sm text-foreground">{v.title}</span>
                  <span className="font-subtitle text-sm text-muted-foreground">
                    {disabled ? "Sold Out" : formatPrice(parseFloat(v.price.amount))}
                  </span>
                </div>
              </button>
            );
          })}
        </div>

        <Button
          variant="hero"
          size="lg"
          className="w-full gap-2 mt-2"
          disabled={!selectedVariantId || isLoading}
          onClick={handleAdd}
        >
          {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShoppingBag className="w-4 h-4" />}
          Add to Cart
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export default VariantPickerDialog;
